import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Settings, ShieldCheck, LogOut } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { msalInstance } from '../../services/msalConfig';

// Avatar + dropdown: identity, Settings, Platform Admin (admins only), Logout.
export default function UserMenu() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleLogout = () => {
    setOpen(false);
    logout();
    // Best-effort MSAL sign-out; ignore if no active account.
    try {
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        msalInstance.logoutRedirect({ account, postLogoutRedirectUri: `${window.location.origin}/login` });
        return;
      }
    } catch {
      /* fall through to local redirect */
    }
    navigate('/login');
  };

  const name = user?.display_name || user?.email || 'User';
  const initials = (user?.display_name || user?.email || 'U')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  const Avatar = ({ size = 'size-8' }: { size?: string }) =>
    user?.photo_url ? (
      <img
        src={user.photo_url}
        alt=""
        className={`${size} rounded-full object-cover ring-1 ring-ws-border`}
      />
    ) : (
      <span
        className={`${size} rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm`}
        style={{
          backgroundImage:
            'linear-gradient(135deg, var(--ws-accent) 0%, var(--ws-dark-teal) 100%)',
        }}
      >
        {initials || 'U'}
      </span>
    );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-ws-surface-2"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar size="size-7" />
        <ChevronDown
          size={14}
          className={`text-ws-ink-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="ws-pop-in absolute right-0 mt-2 w-64 rounded-xl border border-ws-border bg-ws-surface p-1.5 shadow-lg z-50"
        >
          <div className="flex items-center gap-3 px-2.5 py-2.5">
            <Avatar size="size-9" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate text-ws-ink">{name}</div>
              {user?.email && !user?.email_is_placeholder && (
                <div className="text-xs truncate text-ws-ink-3">{user.email}</div>
              )}
            </div>
          </div>
          <div className="my-1 h-px bg-ws-border" />

          <button
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/settings'); }}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink transition-colors"
          >
            <Settings size={16} className="text-ws-ink-3" /> Settings
          </button>

          {user?.is_admin && (
            <button
              role="menuitem"
              onClick={() => { setOpen(false); navigate('/platform-admin'); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink transition-colors"
            >
              <ShieldCheck size={16} className="text-ws-ink-3" /> Platform Admin
            </button>
          )}

          <div className="my-1 h-px bg-ws-border" />
          <button
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ws-danger hover:bg-ws-danger/10 transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
